/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { Send } from 'lucide-react';
import ClienteLayout from '../components/ClienteLayout';
import { interventiAPI } from '../api/interventi';
import { useViewport } from '../hooks/useViewport';

const ACCENT = '#3B82F6';

export default function NuovaRichiesta() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { isMobile } = useViewport();
  const precompilato = location.state?.precompilato || {};
  const [form, setForm] = useState({
    titolo: precompilato.titolo || '',
    descrizione: precompilato.descrizione || '',
    tipo_problema: precompilato.tipo_problema || '',
    priorita: precompilato.priorita || 'normale',
    indirizzo: '', citta: '', cap: '',
    note_cliente: precompilato.note_cliente || '',
  });
  const [error, setError] = useState('');

  const createMutation = useMutation({
    mutationFn: interventiAPI.create,
    onSuccess: (nuovo) => {
      queryClient.invalidateQueries({ queryKey: ['interventi-cliente'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-cliente'] });
      navigate(`/interventi/${nuovo.id}`);
    },
    onError: (err) => setError(err.response?.data?.error || 'Errore nell\'invio della richiesta'),
  });

  const handle = (f) => (e) => { setForm({ ...form, [f]: e.target.value }); setError(''); };

  const submit = (e) => {
    e.preventDefault();
    setError('');
    if (!form.titolo.trim()) { setError('Inserisci un titolo'); return; }
    const payload = { ...form };
    if (precompilato.prodotto_cliente_id) payload.prodotto_cliente_id = precompilato.prodotto_cliente_id;
    if (precompilato.manutenzione_tipo_id) payload.manutenzione_tipo_id = precompilato.manutenzione_tipo_id;
    createMutation.mutate(payload);
  };

  return (
    <ClienteLayout title="Nuova richiesta di intervento">
      <form onSubmit={submit} style={{ ...styles.form, ...(isMobile ? { padding: '1.1rem' } : {}) }}>
        <div style={styles.field}>
          <label style={styles.label}>Di cosa hai bisogno? *</label>
          <input value={form.titolo} onChange={handle('titolo')} style={styles.input} placeholder="es. Perdita rubinetto cucina" required />
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Descrivi il problema</label>
          <textarea value={form.descrizione} onChange={handle('descrizione')} style={{ ...styles.input, minHeight: '100px', fontFamily: 'inherit' }} placeholder="Più dettagli ci dai, meglio possiamo aiutarti..." />
        </div>

        <div style={{ ...styles.row, ...(isMobile ? { flexDirection: 'column' } : {}) }}>
          <div style={styles.field}>
            <label style={styles.label}>Tipo di problema</label>
            <input value={form.tipo_problema} onChange={handle('tipo_problema')} style={styles.input} placeholder="es. Idraulica, Caldaia..." />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Urgenza</label>
            <select value={form.priorita} onChange={handle('priorita')} style={styles.input}>
              <option value="bassa">Bassa - non ho fretta</option>
              <option value="normale">Normale</option>
              <option value="alta">Alta - abbastanza urgente</option>
              <option value="urgente">Urgente - emergenza!</option>
            </select>
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Indirizzo dell'intervento</label>
          <input value={form.indirizzo} onChange={handle('indirizzo')} style={styles.input} placeholder="Via, numero civico" />
        </div>

        <div style={{ ...styles.row, ...(isMobile ? { flexDirection: 'column' } : {}) }}>
          <div style={{ ...styles.field, flex: 2 }}>
            <label style={styles.label}>Città</label>
            <input value={form.citta} onChange={handle('citta')} style={styles.input} />
          </div>
          <div style={{ ...styles.field, flex: 1 }}>
            <label style={styles.label}>CAP</label>
            <input value={form.cap} onChange={handle('cap')} style={styles.input} />
          </div>
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Note aggiuntive</label>
          <textarea value={form.note_cliente} onChange={handle('note_cliente')} style={{ ...styles.input, minHeight: '60px', fontFamily: 'inherit' }} placeholder="Orari preferiti, indicazioni per arrivare, ecc." />
        </div>

        <div style={styles.infoBox}>
          Dopo l'invio potrai aggiungere foto del problema nella pagina di dettaglio.
        </div>

        {error && <div style={styles.errorBox}>{error}</div>}

        <div style={{ ...styles.actions, ...(isMobile ? { flexDirection: 'column-reverse' } : {}) }}>
          <button type="button" onClick={() => navigate('/dashboard')} style={{ ...styles.cancelBtn, ...(isMobile ? { width: '100%' } : {}) }}>Annulla</button>
          <button type="submit" disabled={createMutation.isPending} style={{ ...styles.submitBtn, ...(isMobile ? { width: '100%', justifyContent: 'center' } : {}) }}>
            <Send size={16} /> {createMutation.isPending ? 'Invio...' : 'Invia richiesta'}
          </button>
        </div>
      </form>
    </ClienteLayout>
  );
}

const styles = {
  form: { backgroundColor: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', gap: '1rem', maxWidth: '680px' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.35rem', flex: 1 },
  row: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap' },
  label: { fontSize: '0.85rem', fontWeight: 600, color: '#374151' },
  input: { padding: '0.65rem 0.85rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', width: '100%' },
  infoBox: { backgroundColor: '#EFF6FF', color: '#1E40AF', padding: '0.75rem', borderRadius: '8px', fontSize: '0.85rem' },
  errorBox: { backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.75rem', borderRadius: '8px', fontSize: '0.88rem' },
  actions: { display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' },
  cancelBtn: { padding: '0.65rem 1.2rem', backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', fontWeight: 500 },
  submitBtn: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.65rem 1.4rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 },
};
